import { imgixHost } from 'src/config'
import Model, { encoders, ID, Integer, IsoDate, IsoDateTime, MODEL_LEVEL } from 'src/models/model'
import Librariable from 'src/models/model/concerns/Librariable'
import NewsItemClass from 'src/models/news-item/NewsItemClass'

export default class NewsItem extends Model {
  static name = 'NewsItem'

  static paginate = true
  static deletable = true
  static duplicatable = true
  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.SiteContent]

  static define () {
    this.concerns = [Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      title: {
        type: String,
        required: true,
        translatable: true
      },
      newsItemClassId: {
        type: ID,
        required: true
      },
      newsItemClass: {
        type: NewsItemClass,
        encode: encoders.hasOne,
        reference: true
      },
      displayOrder: {
        type: Integer,
        required: true,
        default: 100
      },
      subtitle: {
        type: String,
        translatable: true
      },
      description: {
        type: String,
        translatable: true
      },
      image: {
        type: String,
        translatable: true
      },
      color: {
        type: String
      },
      hasLink: {
        type: Boolean,
        default: false
      },
      buttonText: {
        type: String,
        translatable: true
      },
      linkUrl: {
        type: String
      },
      imageParams: {
        type: String,
        default: 'fit=crop&w=1000&height=500&auto=format,compress',
        local: true
      },
      imageSmallParams: {
        type: String,
        default: 'fit=crop&w=120&h=60&auto=format,compress',
        local: true
      },
      startDate: {
        type: IsoDate,
        default: null
      },
      endDate: {
        type: IsoDate,
        default: null
      },
      published: {
        type: Boolean,
        default: false
      },
      publishedAt: {
        type: IsoDateTime,
        output: true
      }
    }

    this.computed = {
      current () {
        const date = this.configuration.currentSite.siteDate

        return (!this.startDate || date >= this.startDate.toString()) && (!this.endDate || date <= this.endDate.toString())
      },
      visible () {
        return this.published && this.current
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?${this.imageParams}`
      },
      imageUrlOriginalRatio () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?auto=format,compress`
      },
      imageSmallUrl () {
        if (!this.image) return

        return `${imgixHost}/${this.image}?${this.imageSmallParams}`
      },
      hasAction () {
        return this.hasLink && this.linkUrl
      }
    }

    this.fragments = {
      select: ['id', 'title', 'color']
    }
  }

  get name () {
    return this.title
  }

  route (router) {
    if (!this.hasAction) return

    let url = this.linkUrl

    if (_.startsWith(url, 'http')) {
      window.open(url, '_blank').focus()
      return
    }

    if (_.startsWith(url, '/#/')) url = url.substr(2)

    if (_.startsWith(url, '#/')) url = url.substr(1)

    router.push(url)
  }

  get displayIcons () {
    const icons = []

    if (!this.published) {
      icons.push({
        name: 'toggle_off',
        color: 'grey',
        tooltip: 'Not Published',
        size: 'sm'
      })
      return icons
    }

    if (this.visible && !this.startDate && !this.endDate) {
      icons.push({
        name: 'toggle_on',
        color: 'positive',
        tooltip: 'Published',
        size: 'sm'
      })
    } else if (this.visible) {
      icons.push({
        name: 'visibility',
        color: 'positive',
        tooltip: 'Visible Now',
        size: 'sm'
      })
    } else {
      icons.push({
        name: 'visibility_off',
        color: 'negative',
        tooltip: 'Not Visible',
        size: 'sm'
      })
    }

    if (this.startDate || this.endDate) {
      const now = this.configuration.currentSite.siteTime
      const startDate = this.startDate && this.startDate.inTimeZone(this.configuration.currentSite.timeZone)
      const endDate = this.endDate && this.endDate.inTimeZone(this.configuration.currentSite.timeZone)

      if (startDate && now <= startDate) {
        icons.push({
          name: 'schedule',
          color: 'positive',
          tooltip: `Starts on ${startDate.i18n('date')} (${startDate.relativeTime()})`,
          size: 'sm'
        })
      } else if (endDate && now <= endDate) {
        icons.push({
          name: 'schedule',
          color: 'negative',
          tooltip: `Ends on ${endDate.i18n('date')} (${endDate.relativeTime()})`,
          size: 'sm'
        })
      } else if (endDate && now > endDate) {
        icons.push({
          name: 'schedule',
          color: 'grey',
          tooltip: `Ended on ${endDate.i18n('date')} (${endDate.relativeTime()})`,
          size: 'sm'
        })
      }
    }

    return icons
  }
}
